import styles from "./VCSDisplay.module.css";
import { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import dayjs from "dayjs";
import config from "environment";
import { useTranslation } from "react-i18next";
import { PercentOverTimeGraph } from "./PercentOverTimeGraph/PercentOverTimeGraph";
import { formatPercent } from "./utils/formatPercent";
import { RadialTargetGraph } from "./RadialTargetGraph/RadialTargetGraph";

var utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

/**
 * Displays an IBO's Verified Customer Sales details.
 */
export const VCSDisplay = memo(({ data }) => {
  const { t } = useTranslation();

  const mostRecentMonth = data.monthly
    .map((x) => x)
    .sort((a, b) => (a.volumeMonth > b.volumeMonth ? -1 : 1))[0];

  return (
    <div className={styles.container}>
      <section className={styles.kpiSection}>
        <div className={classNames(styles.kpiChart, styles.targetGraph)}>
          <RadialTargetGraph
            title={t("BSM.verifiedCustomerSales")}
            value={
              mostRecentMonth &&
              mostRecentMonth.salesTotal / mostRecentMonth.personalVolume
            }
            target={0.6}
            max={0.9}
          />
        </div>
        <div className={styles.kpiChart}>
          <PercentOverTimeGraph
            title={t("BSM.verifiedCustomerSales")}
            statName="vcsPercentage"
            data={data.monthly}
          />
        </div>
        <div className={styles.kpiChart}>
          <PercentOverTimeGraph
            title={t("BSM.registeredCustomerBuyers")}
            statName="orderTotal"
            data={data.monthly}
          />
        </div>
      </section>

      <section>
        <header>
          <h3>{t("BSM.Monthly")}</h3>
        </header>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("vcsStats.tableMonthHeader")}</th>
              <th>{t("vcsStats.tableOrdersHeader")}</th>
              <th>{t("vcsStats.tablePVHeader")}</th>
              <th>{t("vcsStats.tableVCSHeader")}</th>
              <th>{t("vcsStats.tablePercentageHeader")}</th>
            </tr>
          </thead>
          <tbody>
            {data.monthly.map((month) => {
              // console.log({ month, name: dayjs(month.volumeMonth) });
              return (
                <tr>
                  <td>
                    <span className={styles.mobileLabel}>
                      {t("vcsStats.tableMonthHeader")}
                    </span>
                    {dayjs.utc(month.volumeMonth).format(config.vcsMonthFormat)}
                  </td>
                  <td>
                    <span className={styles.mobileLabel}>
                      {t("vcsStats.tableOrdersHeader")}
                    </span>
                    {month.orderTotal}
                  </td>
                  <td>
                    <span className={styles.mobileLabel}>
                      {t("vcsStats.tablePVHeader")}
                    </span>
                    {month.personalVolume.toFixed(2)}
                  </td>
                  <td>
                    <span className={styles.mobileLabel}>
                      {t("vcsStats.tableVCSHeader")}
                    </span>
                    {month.salesTotal.toFixed(2)}
                  </td>
                  <td>
                    <span className={styles.mobileLabel}>
                      {t("vcsStats.tablePercentageHeader")}
                    </span>
                    {formatPercent(month.vcsPercentage)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
});

const kpiDiff = PropTypes.shape({
  current: PropTypes.number,
  previous: PropTypes.number,
  change: PropTypes.string,
});

VCSDisplay.propTypes = {
  data: PropTypes.shape({
    monthly: PropTypes.arrayOf(
      PropTypes.shape({
        volumeMonth: PropTypes.string,
        personalVolume: PropTypes.number,
        salesTotal: PropTypes.number,
        orderTotal: PropTypes.number,
        vcsPercentage: PropTypes.number,
      })
    ),
    kpi: PropTypes.shape({
      vcs: kpiDiff,
      buyers: kpiDiff,
      fiscal: kpiDiff,
    }),
  }),
};
